import { type ReactNode } from 'react'
import Link from '@/components/link-component'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'
import { cx } from '@/utils/strings'
import type { Entry } from 'contentful'
import type { TypeComponentPricingSkeleton } from '@/types/ctf'
import HighlightTerms from '@/components/ui/highlight-terms'
import ExpandingBorderCard from '@/components/expanding-border-card'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import Icon from '@/components/icon'

type PricingCardEntry = Entry<TypeComponentPricingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type PricingCardProps = Omit<PricingCardEntry['fields'], 'internalName' | 'subHeading'> & {
  internalName?: string
  subHeading?: PricingCardEntry['fields']['subHeading'] | string
  highlightTerms?: Array<{ id: string; key: string; value: string }>
  priceDivider?: boolean
  fullWidth?: boolean
  peopleCount?: number
  promotionBadge?: string
  autoHighlight?: boolean
  /** Borderless, subgrid-aligned variant used when 4 cards sit in a row. */
  fourUp?: boolean
  /** Extra classes merged onto the 4-up card root (e.g. pair-splitting margins). */
  className?: string
}

export default function PricingCard({
  highlightTerms,
  headline,
  subHeading,
  highlightCard = false,
  actionsPosition = 'Bottom',
  currency,
  pricePretext,
  price,
  priceDivider = false,
  period,
  periodDetail,
  periodDetails,
  peopleCount,
  subtextHeader,
  subtext,
  features,
  bottomText,
  actions = [],
  columnHeading,
  setCardAsLink = false,
  icon,
  iconText,
  promotionBadge,
  fullWidth = false,
  muteFeaturesTick = false,
  autoHighlight = false,
  backgroundColor: _backgroundColor = 'Primary Blue',
  fourUp = false,
  className,
}: PricingCardProps) {
  // 4-up is a borderless/white comparison layout; other layouts keep their assigned colors.
  const backgroundColor = fourUp
    ? 'White'
    : highlightCard || (autoHighlight && !!promotionBadge)
      ? 'Indigo Blue'
      : _backgroundColor
  const hasMultipleActions = actions.length > 1
  // In the 4-up layout the first action stays in the middle; any additional actions drop to the card bottom.
  const secondaryActions = actions.slice(1)

  // Renders the price with any decimal portion raised in a smaller <sup> (e.g. 1.65 → 1.⁶⁵).
  const renderPrice = (value: string | number) => {
    const [whole, fraction] = String(value).split('.')
    return (
      <>
        {whole}
        {fraction && (
          <>
            <sup className="font-normal" style={{ top: '-1rem', fontSize: '1.8rem' }}>
              .{fraction}
            </sup>
          </>
        )}
      </>
    )
  }

  const renderFeatures = () =>
    features && (
      <div className="flex">
        <ul className={cx('list-none text-base', !fourUp && 'mb-4')} data-testid="pricing-features">
          {features.map((feature) => {
            // Match badges in feature that are wrapped with []
            const badges = feature.match(/\[([^\]]+)\]/g) || []
            const featureText = feature.replace(/\[([^\]]+)\]/g, '').trim()

            return (
              <li className="mb-3 last:mb-0 flex flex-row items-start leading-5" key={feature}>
                <Icon
                  className={cx(
                    'text-xl mr-2',
                    (backgroundColor === 'White' || backgroundColor === 'Light Blue') &&
                      cx(fourUp ? 'text-success' : 'text-primaryBlue', muteFeaturesTick && 'text-secondary-400'),
                    (backgroundColor === 'Primary Blue' || backgroundColor === 'Indigo Blue') && 'text-white'
                  )}
                  icon="f140"
                />
                <div className="[&_button]:py-0!">
                  <HighlightTerms
                    text={featureText}
                    terms={highlightTerms}
                    triggerClassName={cx(backgroundColor === 'Primary Blue' && '[&_span]:border-white text-base!')}
                  />
                  {badges.map((badge, index) => (
                    <span
                      key={index}
                      className={cx(
                        'text-[11px] font-medium uppercase ml-2 px-3 py-1 rounded-sm',
                        backgroundColor === 'Primary Blue'
                          ? 'bg-secondary-700 text-white'
                          : 'bg-success text-white dark:text-black'
                      )}
                    >
                      {badge.replace(/[[\]]/g, '')}
                    </span>
                  ))}
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    )

  if (fullWidth) {
    return (
      <div className="flex flex-col gap-3 items-center mt-6">
        <h4 className="h3 header-gradient mb-0 pb-0 leading-tight">{headline}</h4>
        {subHeading && (
          <div className="[&_p]:mb-0">
            {typeof subHeading === 'string' ? subHeading : <RichText body={subHeading} />}
          </div>
        )}
        {renderFeatures()}
        {actions && actions.length >= 1 && (
          <div
            className={cx(hasMultipleActions ? 'flex flex-col gap-4' : '', fullWidth ? 'items-center' : 'items-start')}
          >
            {actions.map((action) => {
              if (!action) return null
              const type = action.sys.contentType.sys.id
              const fields = {
                type,
                ...action.fields,
              } as DynamicActionProps

              return <DynamicAction key={action.sys.id} {...fields} />
            })}
          </div>
        )}
      </div>
    )
  }

  const cardWrapCss = cx(
    'relative p-6 rounded-lg flex flex-col h-full w-full',
    backgroundColor === 'White' &&
      cx('bg-white dark:bg-secondary-800 border dark:border-0', setCardAsLink && 'md:hover:border-transparent'),
    backgroundColor === 'Primary Blue' && 'bg-primary-500 dark:bg-primary-600 text-white',
    backgroundColor === 'Indigo Blue' && 'bg-indigoBlue text-white',
    backgroundColor === 'Light Blue' && 'bg-bw-light-blue dark:bg-secondary-800'
  )

  const LinkWrap = ({ children, ctas }: { children: ReactNode; ctas: any }) => (
    <>
      {columnHeading && <div className="xl:hidden text-xl py-2 text-white">{columnHeading}</div>}
      {setCardAsLink && ctas && ctas.length === 1 && ctas[0].fields.link ? (
        <ExpandingBorderCard
          borderSize="sm"
          borderColor={null} // set to null so that the border color is determined by the background color of the card
          className={cx(
            'mb-4 h-full w-full max-w-2xl mx-auto',
            backgroundColor === 'White' && 'before:border-primaryBlue dark:before:border-secondary-800',
            backgroundColor === 'Primary Blue' && 'before:border-primary-500 dark:before:border-primary-600',
            backgroundColor === 'Indigo Blue' && 'before:border-indigoBlue',
            backgroundColor === 'Light Blue' && 'before:border-bw-light-blue dark:before:border-secondary-800'
          )}
        >
          <Link className={cx(cardWrapCss, ctas[0].fields.plausibleEventName)} to={ctas[0].fields.link.fields.url}>
            {children}
          </Link>
        </ExpandingBorderCard>
      ) : (
        <div className={cardWrapCss}>{children}</div>
      )}
    </>
  )

  const linkLikeStyles: string[] = [
    buttonStyles.btnArrow,
    buttonStyles.btnGhost,
    buttonStyles.btnPlain,
    buttonStyles.btnText,
  ]
  const highlightedTextStyles: string[] = [buttonStyles.btnArrow, buttonStyles.btnPlain, buttonStyles.btnText]

  const renderActions = (actionsToRender: typeof actions = actions) => (
    <div
      className={cx(hasMultipleActions ? 'flex flex-col gap-6' : 'w-full', fullWidth ? 'items-center' : 'items-start')}
    >
      {actionsToRender.map((action) => {
        if (!action) return null
        // Use the action's position in the FULL list so styling stays stable when we render a subset
        // (first action = solid, later actions keep their own style) — e.g. the 2nd action moved to the bottom.
        const i = actions.indexOf(action)
        const type = action.sys.contentType.sys.id
        const fields = {
          type,
          ...action.fields,
        } as DynamicActionProps

        // first button: btnSolidSecondary on highlighted cards, btnSolid otherwise; others (and every
        // button in the 4-up layout) use their own style
        const effectiveStyle =
          i === 0 && !fourUp
            ? backgroundColor === 'Primary Blue'
              ? buttonStyles.btnSolidSecondary
              : buttonStyles.btnSolid
            : fields.style
        const isLinkLike = linkLikeStyles.includes(effectiveStyle)

        return setCardAsLink ? (
          <CtaLink
            to={!setCardAsLink || actions.length > 1 ? action.fields?.link?.fields?.url : undefined}
            style={effectiveStyle}
            key={action.sys.id}
            className={cx(
              !isLinkLike && 'w-full [&_>span]:justify-center',
              backgroundColor === 'Primary Blue' &&
                highlightedTextStyles.includes(fields.style) &&
                'text-white! hover:text-white! dark:text-primaryBlue!'
            )}
          >
            {fields.label}
          </CtaLink>
        ) : (
          <DynamicAction
            to={!setCardAsLink || actions.length > 1 ? action.fields?.link?.fields?.url : undefined}
            key={action.sys.id}
            {...fields}
            style={effectiveStyle}
            className={cx(
              !isLinkLike && 'w-full [&_>span]:justify-center',
              // link-like buttons on a highlighted card need light text so they stay legible on the dark bg
              backgroundColor === 'Primary Blue' &&
                highlightedTextStyles.includes(fields.style) &&
                'text-white! hover:text-white! dark:text-primaryBlue!'
            )}
          />
        )
      })}
    </div>
  )

  if (fourUp) {
    // Whole-card link only when there's a single linked CTA; otherwise a plain container.
    const soleAction: any = actions?.length === 1 ? actions[0] : null
    const linkCta = setCardAsLink && soleAction?.fields?.link ? soleAction : null
    const RootTag: any = linkCta ? Link : 'div'
    const rootProps: any = linkCta ? { to: linkCta.fields.link?.fields.url } : {}

    // At lg (four columns) the cards share the container's subgrid rows (headline / price / actions / body)
    // so equivalent sections line up. Below lg the container is a 2-up grid whose row tracks don't match a
    // 5-row span, so the card is a plain flex column there (stacked sections, no cross-card alignment) —
    // otherwise `row-span-5` pushes cards 3–4 into a second 5-row group and opens a large gap.
    return (
      <RootTag
        {...rootProps}
        className={cx(
          'flex flex-col gap-y-4 h-full px-6 xl:px-11 py-8 md:py-0',
          'lg:grid lg:grid-rows-subgrid lg:row-span-5',
          'text-sm text-secondary-900 dark:text-white',
          // Only a single-CTA card is a whole-card link; `group` lets that CTA pick up the card's hover
          // state (mirrors the non-4-up layout). With multiple links the card isn't clickable, so no group.
          linkCta && 'group',
          linkCta?.fields?.plausibleEventName,
          className
        )}
      >
        {/* Row 1 — headline + subheading */}
        <div className="relative">
          {promotionBadge && (
            <div className="absolute top-0 right-0 uppercase text-xs font-[500] px-3 py-1 rounded-full bg-primaryBlue text-white">
              {promotionBadge}
            </div>
          )}

          <h4 className="pt-0 h6 mb-0 pb-0 font-bold" data-testid="pricing-headline">
            {icon && <Icon className="text-4xl mb-2 block!" icon={icon.fields.unicode} />}
            {headline}
          </h4>
          {subHeading && (
            <div className="mt-2 [&_p]:mb-0 [&_p]:text-sm">
              {typeof subHeading === 'string' ? subHeading : <RichText body={subHeading} />}
            </div>
          )}
        </div>

        {/* Row 2 — price + period (or icon fallback) */}
        <div>
          {price && (
            <>
              {pricePretext && <div className="text-sm mb-1">{pricePretext}</div>}
              <div className="flex flex-col gap-1">
                <div className="font-bold text-[3.25rem] leading-0" data-testid="pricing-price">
                  {currency?.trim() && (
                    <span className="leading-tight" data-testid="pricing-currency">
                      {currency}
                    </span>
                  )}
                  <span className="leading-tight">{renderPrice(price)}</span>
                </div>
                <div className="text-sm">
                  {period && <span data-testid="pricing-period">{period}</span>}
                  {periodDetails ? (
                    <span data-testid="pricing-periodDetail" className="[&_p]:text-sm [&_p]:mb-0 opacity-70">
                      <RichText body={periodDetails} />
                    </span>
                  ) : periodDetail ? (
                    <span data-testid="pricing-periodDetail" className="text-sm opacity-70">
                      {periodDetail}
                    </span>
                  ) : null}
                </div>
              </div>
              {peopleCount && (
                <p className="text-sm flex mt-2 gap-1 flex-row items-center">
                  {peopleCount} {peopleCount === 1 ? 'person = ' : 'people = '}
                  <span className="font-semibold">
                    {currency}
                    {Number(price) * peopleCount}/month
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        {/* Row 3 — primary action (kept in the middle) */}
        <div>{actions && actions.length >= 1 ? renderActions(actions.slice(0, 1)) : null}</div>

        {/* Row 4 — subtext + features + bottom text */}
        <div className="flex flex-col [&_ul]:text-sm!">
          {(subtext || subtextHeader) && (
            <span className="text-sm font-semibold min-h-[40px]" data-testid="pricing-subtext">
              {subtextHeader && <span className="block">{subtextHeader}</span>}
              {subtext}
            </span>
          )}
          {renderFeatures()}
          {bottomText && (
            <span
              className="text-sm font-medium text-secondary-600 dark:text-secondary-200 block mt-4"
              data-testid="pricing-bottom-text"
            >
              <HighlightTerms text={bottomText} terms={highlightTerms} />
            </span>
          )}
        </div>

        {/* Row 5 — any additional actions, below all details (empty div keeps the subgrid rows aligned
            across cards even when a card has no second action). */}
        <div>{secondaryActions.length > 0 ? renderActions(secondaryActions) : null}</div>
      </RootTag>
    )
  }

  return (
    <LinkWrap ctas={actions}>
      <div className="flex flex-col h-full">
        {promotionBadge && (
          <div
            className={cx(
              'absolute top-4 right-4 uppercase text-xs font-[500] px-3 py-1 rounded-full bg-secondary-700 text-white',
              backgroundColor === 'White' && 'dark:bg-white dark:text-black'
            )}
          >
            {promotionBadge}
          </div>
        )}
        <div className="flex flex-col gap-6">
          <div
            className={cx(
              'flex flex-col gap-6 flex-grow',
              backgroundColor === 'White' || backgroundColor === 'Light Blue'
                ? 'text-black dark:text-white'
                : 'text-white'
            )}
          >
            <h4
              className={cx('h6 mb-0 pb-0 ', backgroundColor === 'Light Blue' && 'text-primaryBlue')}
              data-testid="pricing-headline"
            >
              {headline}
            </h4>
            {subHeading && (
              <div className="-mt-4 [&_p]:mb-0 [&_p]:text-base">
                {typeof subHeading === 'string' ? subHeading : <RichText body={subHeading} />}
              </div>
            )}
            {actions && actions.length >= 1 && actionsPosition === 'Above' && renderActions()}

            {/* Price and period / period detail */}
            {price ? (
              <div className={pricePretext && '-mt-5'}>
                {pricePretext && (
                  <div
                    className={cx(
                      backgroundColor === 'Primary Blue' ? 'text-secondary-50' : ' text-primaryBlue',
                      'h-5 text-sm'
                    )}
                  >
                    {pricePretext}
                  </div>
                )}
                <div
                  className={cx(
                    'flex flex-row items-center gap-4 h-17',
                    priceDivider && 'py-13 border-t border-b',
                    priceDivider &&
                      backgroundColor === 'Primary Blue' &&
                      'border-primary-700 dark:border-secondary-800',
                    priceDivider && backgroundColor === 'White' && 'border-secondary-100'
                  )}
                >
                  <div
                    className={cx('', backgroundColor === 'White' && 'text-primaryBlue')}
                    data-testid="pricing-price"
                  >
                    <div className="font-medium">
                      {currency?.trim() && (
                        <sup className="text-[2.5rem] leading-[4rem] align-super" data-testid="pricing-currency">
                          {currency}
                        </sup>
                      )}
                      <span className="leading-[4rem] text-[4rem]">{price}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 text-lg">
                    {period && <span data-testid="pricing-period">{period}</span>}
                    {periodDetails ? (
                      <span
                        data-testid="pricing-periodDetail"
                        className="[&_p]:text-sm [&_p]:mb-0 text-secondary-100 dark:text-secondary-200"
                      >
                        <RichText body={periodDetails} />
                      </span>
                    ) : periodDetail ? (
                      <span data-testid="pricing-periodDetail" className="text-sm opacity-70">
                        {periodDetail}
                      </span>
                    ) : null}
                  </div>
                </div>
                {peopleCount && (
                  <p className={cx('text-sm flex mt-2 mb-4 gap-1 flex-row items-center')}>
                    {peopleCount} {peopleCount === 1 ? 'person = ' : 'people = '}
                    <span className="text-xl font-semibold">
                      {currency}
                      {Number(price) * peopleCount}/month
                    </span>
                  </p>
                )}
              </div>
            ) : (
              <>
                {icon && (
                  <div>
                    <Icon
                      className={cx(
                        'text-6xl ',
                        backgroundColor === 'Primary Blue' ? 'text-white' : 'text-primaryBlue'
                      )}
                      icon={icon.fields.unicode}
                    />
                  </div>
                )}
                {iconText && (
                  <div>
                    <p className={cx('text-sm flex mt-2 mb-0', backgroundColor === 'Primary Blue' && 'text-white')}>
                      {iconText}
                    </p>
                  </div>
                )}
              </>
            )}
            {actions && actions.length >= 1 && actionsPosition === 'Middle' && renderActions()}
          </div>
          <div
            className={cx(
              'flex flex-col gap-4 flex-grow',
              backgroundColor === 'White' || backgroundColor === 'Light Blue'
                ? 'text-black dark:text-white'
                : 'text-white'
            )}
          >
            {(subtext || subtextHeader) && (
              <span className="text-base font-semibold" data-testid="pricing-subtext">
                {subtextHeader && <span className="text-lg font-semibold block">{subtextHeader}</span>}
                {subtext}
              </span>
            )}

            {renderFeatures()}
            {bottomText && (
              <span
                className={cx(
                  'text-sm font-medium m-0',
                  backgroundColor === 'White' && 'text-secondary-600 dark:text-secondary-200',
                  backgroundColor === 'Primary Blue' && 'text-secondary-100 dark:text-secondary-200',
                  backgroundColor === 'Indigo Blue' && 'text-secondary-200',
                  backgroundColor === 'Light Blue' && 'text-secondary-600 dark:text-secondary-200'
                )}
                data-testid="pricing-bottom-text"
              >
                <HighlightTerms text={bottomText} terms={highlightTerms} />
              </span>
            )}
          </div>
        </div>
        {actions && (actionsPosition === 'Bottom' || actionsPosition === null) && (
          <div className={cx('pt-6 mt-auto flex items-start w-full', hasMultipleActions && 'xl:min-h-[120px]')}>
            {renderActions()}
          </div>
        )}
      </div>
    </LinkWrap>
  )
}
